/**
 * @ngdoc service
 * @name app.apiService
 * @description
 * # API Service
 * Main service to manage API communication.
 * It provides standarization for network calls.
 * @requires constants:config
 * @requires constants:cacheExpiry
 * @requires app:localService
 * @requires ng.$http
 **/
/*---------------------------------------------------------*\
 * Imports
\*---------------------------------------------------------*/
import angular from 'angular';
import uuid from 'uuid';
import _ from 'lodash';

/*---------------------------------------------------------*\
 * apiService
\*---------------------------------------------------------*/
class ApiService {
  constructor($http, localService, cacheExpiry, config, storageKeys) {
    // get cache expiration from config service. If not set then resort to default constant
    const cacheExpiryValMs = config.sessionStorageCacheExpiryMs ?
      config.sessionStorageCacheExpiryMs :
      cacheExpiry.default;
    const defaultCache = localService.createCache('$http', {
      deleteOnExpire: true,
      maxAge: cacheExpiryValMs,
      storeOnResolve: true
    });

    const GET = 'get';
    const POST = 'post';

    const _wrap = (url, config, resultName, cachebuster, method, data) => {
      let promise;
      const mockData = localService.get(storageKeys.mockData);

      if (cachebuster) {
        url = this.addQueryParamToUrl(url, 'cachebuster', uuid.v4());
      } else {
        // if cachebuster flag is set we will not only bust the cache in the url
        // but also make sure it's not locally cached
        config.cache = config.cache || defaultCache;
      }

      if (mockData) {
        url = this.addQueryParamToUrl(url, 'mockdata', mockData);
      }

      if (method.toLowerCase() === GET) {
        promise = $http.get(url, config);
      } else if (method.toLowerCase() === POST) {
        promise = $http.post(url, data, config);
      }

      if (resultName) {
        promise.then((result) => {
          this[resultName + 'Result'] = result;
        });
      }

      promise.catch((error) => {
        console.error('## DATA ## API ERROR ' + resultName, url, error);

        if (resultName) {
          this[resultName + 'Error'] = error;
        }
      });

      return promise;
    };

    this.addQueryParamToUrl = (url, param, paramValue) => {
      const prefix = _.contains(url, '?') ? '&' : '?';
      return url + prefix + param + '=' + paramValue;
    };

    /**
     * @ngdoc
     * @name app.apiService#wrap
     * @methodOf app.apiService
     * @description
     * apiService.wrap
     * Wraps a GET API call, providing standard methodology to handle
     *
     * - Console logging
     * - Callbacks
     *
     * See waitFor for information on listening for when a call has been completed.
     *
     * @param {string} url to hit with a GET request.
     * @param {object} config Configuration to pass to the $http.get() method.
     * @param {string} resultName The name used to store the result, it's promise, or it's error.
     * @param {boolean} cachebuster Whether to append a cachebuster query param or not.
     *
     * @returns {httpPromise} Promise
     */
    this.wrap = (url, config, resultName, cachebuster) => {
      return _wrap(url, config, resultName, cachebuster, GET);
    };

    /**
     * @ngdoc
     * @name app.apiService#wrapPost
     * @methodOf app.apiService
     * @description
     * apiService.wrapPost
     * Wraps a POST API call, providing standard methodology to handle
     *
     * - Console logging
     * - Callbacks
     *
     * See waitFor for information on listening for when a call has been completed.
     *
     * @param {string} url The url to hit with a GET request.
     * @param {object} data The data to send to the backend
     * @param {object} config Configuration to pass to the $http.get() method.
     * @param {string} resultName The name used to store the result, it's promise, or it's error.
     * @param {boolean} cachebuster Whether to append a cachebuster query param or not.
     *
     * @returns {httpPromise} Promise
     */
    this.wrapPost = (url, data, config, resultName, cachebuster) => {
      return _wrap(url, config, resultName, cachebuster, POST, data);
    };
  }

  //-----------------------------------
  // Static
  //-----------------------------------
  static factory($http, localService, cacheExpiry, config, storageKeys) {
    return new ApiService($http, localService, cacheExpiry, config, storageKeys);
  }

}

ApiService.factory.$inject = ['$http', 'localService', 'cacheExpiry', 'config', 'storageKeys'];

const api = angular.module('apiService', ['constants', 'localService', 'config'])
  .factory('apiService', ApiService.factory);

export default api;



/** WEBPACK FOOTER **
 ** ./client/app/services/apis/api.js
 **/